import { Check, Copy, Link2, Search, Send, UserPlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ScrollArea,
} from "@link/ui";

import { api } from "../lib/api";
import { requestDirectMessage } from "../lib/dm-navigation";
import type { SpaceView } from "../types/ui";

interface InviteFriend {
  xid: string;
  name: string;
  username?: string;
  avatarUrl: string | null;
}

export function ServerInviteDialog({
  open,
  space,
  onOpenChange,
}: {
  open: boolean;
  space: SpaceView;
  onOpenChange: (open: boolean) => void;
}) {
  const [friends, setFriends] = useState<InviteFriend[]>([]);
  const [query, setQuery] = useState("");
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setQuery("");
    setInviteUrl(null);
    setError(null);
    setCopied(false);
    setLoading(true);

    void Promise.all([api.friends.list(), api.spaces.createInvite(space.xid)])
      .then(([friendResult, invite]) => {
        if (cancelled) return;
        setFriends(
          friendResult.friends
            .filter((friend) => friend.status === "accepted")
            .map((friend) => ({
              xid: friend.account_xid,
              name: friend.display_name ?? friend.username ?? friend.account_xid,
              ...(friend.username !== undefined
                ? { username: friend.username }
                : {}),
              avatarUrl: friend.avatar_url,
            })),
        );
        setInviteUrl(`${window.location.origin}/invite/${invite.xid}`);
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        setError(
          reason instanceof Error
            ? reason.message
            : "Couldn't prepare an invite right now.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, space.xid]);

  const visibleFriends = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (normalized.length === 0) return friends;
    return friends.filter(
      (friend) =>
        friend.name.toLowerCase().includes(normalized) ||
        friend.username?.toLowerCase().includes(normalized) === true,
    );
  }, [friends, query]);

  const copyInvite = async (): Promise<void> => {
    if (inviteUrl === null) return;
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(680px,calc(100dvh-1rem))] max-w-lg flex-col overflow-hidden border-cloud-300 bg-white p-0 text-cloud-700 shadow-2xl">
        <DialogHeader className="px-6 pb-3 pt-6 text-left">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-strawberry-gradient text-white shadow-lg shadow-strawberry-300/30">
            <UserPlus className="h-5 w-5" />
          </div>
          <DialogTitle className="text-xl text-cloud-900">
            Invite friends to {space.name}
          </DialogTitle>
          <DialogDescription className="text-cloud-500">
            Pick a friend or share the invite link. The link expires in seven days.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-3">
          <div className="flex items-center gap-2 rounded-xl border border-cloud-300 bg-cloud-50 px-3 transition-shadow focus-within:border-strawberry-300 focus-within:ring-2 focus-within:ring-strawberry-100">
            <Search className="h-4 w-4 text-cloud-400" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search friends"
              className="h-11 min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-cloud-700 outline-none placeholder:text-cloud-400"
            />
          </div>
        </div>

        <ScrollArea className="min-h-0 flex-1 px-4">
          <div className="space-y-1 px-2 pb-4">
            {loading ? (
              <p className="py-8 text-center text-sm text-cloud-500">
                Preparing your invite…
              </p>
            ) : error !== null ? (
              <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200">
                {error}
              </p>
            ) : visibleFriends.length === 0 ? (
              <p className="py-8 text-center text-sm text-cloud-500">
                {friends.length === 0
                  ? "You don't have any friends to invite yet. You can still copy the link below."
                  : "No friends match that search."}
              </p>
            ) : (
              visibleFriends.map((friend) => (
                <div
                  key={friend.xid}
                  className="flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-cloud-100"
                >
                  <Avatar className="h-10 w-10">
                    {friend.avatarUrl !== null ? (
                      <AvatarImage src={friend.avatarUrl} alt="" />
                    ) : null}
                    <AvatarFallback className="text-xs">
                      {friend.name.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-cloud-800">
                      {friend.name}
                    </p>
                    {friend.username !== undefined ? (
                      <p className="truncate text-xs text-cloud-500">
                        @{friend.username}
                      </p>
                    ) : null}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={inviteUrl === null}
                    onClick={() => {
                      if (inviteUrl === null) return;
                      requestDirectMessage(
                        friend.xid,
                        `Join ${space.name} on Link: ${inviteUrl}`,
                        true,
                      );
                      onOpenChange(false);
                    }}
                  >
                    <Send className="mr-1.5 h-3.5 w-3.5" />
                    Invite
                  </Button>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        <div className="border-t border-cloud-200 bg-cloud-50 px-6 py-5">
          <p className="mb-2 text-xs font-semibold text-cloud-700">
            Or send an invite link
          </p>
          <div className="flex items-center gap-2 rounded-xl border border-cloud-300 bg-white p-1.5">
            <Link2 className="ml-2 h-4 w-4 flex-none text-cloud-400" />
            <input
              readOnly
              value={inviteUrl ?? "Preparing invite link…"}
              className="min-w-0 flex-1 truncate bg-transparent text-xs text-cloud-600 outline-none"
              aria-label="Server invite link"
            />
            <Button size="sm" disabled={inviteUrl === null} onClick={() => void copyInvite()}>
              {copied ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
